import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import FilterDropdowns from '@/pages/Shipments/Filters/FilterDropdowns.js';
import {
  CalendarFilterMenu,
  MainFilterMenu,
  SecondaryFilterOption,
} from '@/pages/Shipments/Filters/FilterDropdownMenus.js';
import SubFilterMenu from '@/pages/Shipments/Filters/components/SubFilterMenu';

import {
  getServiceLevelCouriersDDL,
  getTeamsDDLOFFeature,
} from '@/common/utils/dropDown.js';
import {
  prependNoTeamOption,
  realTeamOptions,
} from '@/common/utils/noTeamFilter';
import { useCanShowNoTeamFilter } from '@/hooks/useCanShowNoTeamFilter';
import { filtersConfig } from '@/common/static_data';
import useToken from '@/common/helper/useToken';
import useUnauthorize from '@/hooks/useUnauthorize';

import { OptionItem } from '../../../Data/components/DownloadReportFilterSection';
import { CheckboxChangeEvent } from 'antd';
import { themeT } from '@/utils/themeText';
import theme from '@/assets/theme';
import {
  DEFAULT_FILTER_LOGIC,
  getAppliedFilterLogic,
  normalizeFilterLogic,
  supportsFilterLogic,
} from '@/pages/Shipments/Filters/filterLogic';
import type { FilterLogic } from '@/pages/Shipments/Filters/filterLogic';
import type {
  ListItem,
  SecondaryFilterOption as SubFilterOption,
  SubmenuAnchor,
} from '@/pages/Shipments/ControlTower/types';
import './TeamCourierFilters.css';

type FilterValue = string | number;

export interface FilterArrayItem {
  filterBy: string;
  filterValue: FilterValue[];
  logic?: FilterLogic;
}

interface TeamCourierFiltersProps {
  onFiltersChange?: (filterArray: FilterArrayItem[]) => void;
  initialTeams?: Array<string | number>;
  initialServiceLevels?: Array<string | number>;
  /** Prefill every currently-loaded team directly, instead of matching initialTeams ids one by one. */
  teamsSelectAll?: boolean;
  /** Prefill every currently-loaded courier/service-level (plus the synthetic "Unbooked" option) directly. */
  serviceLevelsSelectAll?: boolean;
  couriers?: CourierOption[];
  teamsList?: TeamOption[];
  innerAPICall?: boolean;
}

/** Synthetic courier-less option SubFilterBody.tsx always injects alongside real couriers. */
const NO_COURIER_OPTION = { id: '__NO_COURIER__', name: 'Unbooked' } as const;

interface FilterDDLItem {
  id: number | string;
  key: string;
  label: string;
}

interface SecondaryOption {
  id: string | number;
  name?: string;
  label?: string;
  [key: string]: unknown;
}

interface AppliedFilter extends Omit<SecondaryOption, 'id'> {
  id: string;
  filterBy: string;
  groupTitle: string;
  logic?: FilterLogic;
}

interface ApplyOverride {
  filterBy?: string;
  items?: SecondaryOption[];
  groupTitle?: string;
  skipUserInteraction?: boolean;
}

export interface TeamOption {
  id: string | number;
  name: string;
}

interface CourierServiceLevel {
  id: string | number;
  display_name: string;
}

export interface CourierOption {
  id: string | number;
  name: string;
  service_levels?: CourierServiceLevel[];
}

const TeamCourierFilters: React.FC<TeamCourierFiltersProps> = ({
  onFiltersChange,
  initialTeams,
  initialServiceLevels,
  teamsSelectAll = false,
  serviceLevelsSelectAll = false,
  couriers = [],
  teamsList = [],
  innerAPICall = true,
}) => {
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const didFetchRef = useRef(false);

  const courierFilter = useMemo(
    () =>
      filtersConfig.groupedFilters
        .flatMap((group) => group.items)
        .find((item) => item.key === 'courier'),
    []
  );

  const pageTopLevelFilters = useMemo(
    () => filtersConfig.topLevelFilters.filter((item) => item.key === 'team'),
    []
  );

  const pageGroupedFilters = useMemo(
    () =>
      courierFilter
        ? [
            {
              group: 'Shipment',
              items: [courierFilter],
            },
          ]
        : [],
    [courierFilter]
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [secondFilterDisplay, setSecondFilterDisplay] = useState(false);
  const [filterBy, setFilterBy] = useState('');
  const [filterTitle, setFilterTitle] = useState('');
  const [openedFromPill, setOpenedFromPill] = useState(false);
  const [submenuAnchor] = useState<SubmenuAnchor | undefined>(undefined);
  const [filterLogic, setFilterLogic] =
    useState<FilterLogic>(DEFAULT_FILTER_LOGIC);

  const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<ListItem[]>([]);
  const [filterArray, setFilterArray] = useState<FilterArrayItem[]>([]);
  const [secondaryFiltersDDL, setSecondaryFiltersDDL] = useState<
    SubFilterOption[]
  >([]);
  const [secondaryFiltersSource, setSecondaryFiltersSource] = useState<
    OptionItem[]
  >([]);

  const [editingId] = useState(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const [couriersList, setCouriersList] = useState<CourierOption[]>([]);
  const [rawTeams, setRawTeams] = useState<TeamOption[]>([]);
  const canShowNoTeamFilter = useCanShowNoTeamFilter();
  const allTeams = useMemo(
    () => prependNoTeamOption(rawTeams, canShowNoTeamFilter),
    [rawTeams, canShowNoTeamFilter]
  );

  useEffect(() => {
    if (!token) return;
    if (didFetchRef.current) return;

    if (couriers.length > 0) {
      setCouriersList(couriers);
    }
    if (teamsList.length > 0) {
      setRawTeams(realTeamOptions(teamsList));
    }

    if (innerAPICall && couriers.length === 0 && teamsList.length === 0) {
      getTeamsDDLOFFeature(token, 'Shipment')
        .then((val) => {
          if (Array.isArray(val)) {
            setRawTeams(val as TeamOption[]);
          }
        })
        .catch(unauthorize);

      getServiceLevelCouriersDDL(token)
        .then((val) => {
          if (Array.isArray(val)) setCouriersList(val as CourierOption[]);
        })
        .catch(unauthorize);
    }

    didFetchRef.current = true;
  }, [token, unauthorize, innerAPICall, couriers, teamsList]);

  useEffect(() => {
    if (teamsList.length > 0) {
      setRawTeams(realTeamOptions(teamsList));
    }
  }, [teamsList]);

  const setBoth = useCallback(
    (list: unknown) => {
      const safe = Array.isArray(list) ? list : [];
      setSecondaryFiltersSource(safe as OptionItem[]);
      // the dropdown helpers return untyped option lists
      setSecondaryFiltersDDL(safe as SubFilterOption[]);
    },
    [setSecondaryFiltersDDL, setSecondaryFiltersSource]
  );

  const configureForFilterKey = useCallback(
    (fb: string) => {
      setIsFilterOpen(false);
      setBoth([]);

      switch (fb) {
        case 'courier':
          setFilterTitle(themeT('transportProvider', theme.themeType));
          setBoth(couriersList);
          break;
        case 'team':
          setFilterTitle(themeT('team', theme.themeType));
          setBoth(prependNoTeamOption(allTeams, canShowNoTeamFilter));
          break;
        default:
          setFilterTitle(
            fb?.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
          );
          setBoth([]);
      }
    },
    [allTeams, canShowNoTeamFilter, couriersList, setBoth]
  );

  const handleApplyFilters = useCallback(
    (override: ApplyOverride = {}) => {
      const fb = override.filterBy ?? filterBy;
      const items: Array<SecondaryOption | ListItem> =
        override.items ?? selectedOptions ?? [];
      const groupTitle = override.groupTitle ?? filterTitle;
      if (!fb) return;
      const logic = normalizeFilterLogic(filterLogic);

      if (!override.skipUserInteraction) {
        setHasUserInteracted(true);
      }

      const desiredIds = items.map((o) => o.id);
      // Prefilled team / courier options can carry numeric ids; the sub-filter
      // menu compares ids as strings, so it reads them the same either way.
      setSelectedOptions(items as ListItem[]);

      setAppliedFilters((prev) => {
        const others = prev.filter((a) => a.filterBy !== fb);
        if (!desiredIds.length) return others;

        const newOnes: AppliedFilter[] = items.map((o) => ({
          ...o,
          id: String(o.id),
          filterBy: fb,
          groupTitle,
          ...(supportsFilterLogic(fb) ? { logic } : {}),
        }));

        return [...others, ...newOnes];
      });

      setFilterArray((prevFA) => {
        const othersFA = (prevFA || []).filter((f) => f.filterBy !== fb);

        const nextFilterArray: FilterArrayItem[] = desiredIds.length
          ? [
              ...othersFA,
              {
                filterBy: fb,
                filterValue: desiredIds,
                ...(supportsFilterLogic(fb) ? { logic } : {}),
              },
            ]
          : othersFA;

        if (onFiltersChange) {
          onFiltersChange(nextFilterArray);
        }

        return nextFilterArray;
      });

      setSecondFilterDisplay(false);
      setIsFilterOpen(false);
    },
    [filterBy, filterTitle, filterLogic, selectedOptions, onFiltersChange]
  );

  const [prefilledFromHome, setPrefilledFromHome] = useState(false);
  const [dataReady, setDataReady] = useState(false);

  // Check if data is ready for initialization
  useEffect(() => {
    const hasInitialTeams =
      teamsSelectAll ||
      (Array.isArray(initialTeams) && initialTeams.length > 0);
    const hasInitialServiceLevels =
      serviceLevelsSelectAll ||
      (Array.isArray(initialServiceLevels) && initialServiceLevels.length > 0);
    const hasTeamsData = teamsList.length > 0;
    const hasCouriersData = couriers.length > 0;

    // Only consider data ready if:
    // 1. We have no initial filters, OR
    // 2. We have initial filters AND corresponding data to match them
    const isDataReady =
      (!hasInitialTeams && !hasInitialServiceLevels) || // No initial filters
      (hasInitialTeams && hasTeamsData) || // Has teams and teams data
      (hasInitialServiceLevels && hasCouriersData); // Has service levels and couriers data

    setDataReady(isDataReady);
  }, [
    couriers,
    teamsList,
    initialTeams,
    initialServiceLevels,
    teamsSelectAll,
    serviceLevelsSelectAll,
  ]);

  useEffect(() => {
    if (prefilledFromHome || hasUserInteracted || !dataReady) return;

    const hasTeams =
      teamsSelectAll ||
      (Array.isArray(initialTeams) && initialTeams.length > 0);
    const hasLevels =
      serviceLevelsSelectAll ||
      (Array.isArray(initialServiceLevels) && initialServiceLevels.length > 0);

    if ((hasTeams && !allTeams.length) || (hasLevels && !couriersList.length)) {
      return;
    }

    const initialTeamIds =
      hasTeams && !teamsSelectAll ? initialTeams!.map((id) => String(id)) : [];

    const initialLevelIds =
      hasLevels && !serviceLevelsSelectAll
        ? initialServiceLevels!.map((id) => String(id))
        : [];

    if (hasTeams && allTeams.length) {
      const teamItems: SecondaryOption[] = teamsSelectAll
        ? allTeams.map((t) => ({ id: String(t.id), name: t.name }))
        : allTeams
            .filter((t) => initialTeamIds.includes(String(t.id)))
            .map((t) => ({
              id: String(t.id),
              name: t.name,
            }));

      if (teamItems.length) {
        handleApplyFilters({
          filterBy: 'team',
          items: teamItems,
          groupTitle: 'Team',
          skipUserInteraction: true,
        });
      }
    }

    if (hasLevels && couriersList.length) {
      // Resolves one explicit id against couriersList (or the synthetic
      // "Unbooked" option, which never lives in couriersList itself).
      const resolveLevelId = (rawId: string | number): SecondaryOption[] => {
        const idStr = String(rawId);

        if (idStr === NO_COURIER_OPTION.id) {
          return [{ ...NO_COURIER_OPTION }];
        }

        for (const courier of couriersList) {
          const levelMatch = courier.service_levels?.find(
            (s) => String(s.id) === idStr
          );
          if (levelMatch) {
            return [
              {
                id: String(levelMatch.id),
                name: `${courier.name} - ${levelMatch.display_name}`,
              },
            ];
          }
        }

        const courierMatch = couriersList.find((c) => String(c.id) === idStr);

        if (courierMatch && courierMatch.service_levels?.length) {
          return courierMatch.service_levels.map((level) => ({
            id: String(level.id),
            name: `${courierMatch.name} - ${level.display_name}`,
          }));
        }

        return [];
      };

      const levelItems: SecondaryOption[] = serviceLevelsSelectAll
        ? [
            ...couriersList.flatMap((courier) =>
              courier.service_levels?.length
                ? courier.service_levels.map((level) => ({
                    id: String(level.id),
                    name: `${courier.name} - ${level.display_name}`,
                  }))
                : [{ id: String(courier.id), name: courier.name }]
            ),
            { ...NO_COURIER_OPTION },
          ]
        : initialLevelIds.flatMap(resolveLevelId);

      if (levelItems.length) {
        handleApplyFilters({
          filterBy: 'courier',
          items: levelItems,
          groupTitle: 'Transport provider',
          skipUserInteraction: true,
        });
      }
    }

    setPrefilledFromHome(true);
  }, [
    initialTeams,
    initialServiceLevels,
    teamsSelectAll,
    serviceLevelsSelectAll,
    allTeams,
    couriersList,
    handleApplyFilters,
    prefilledFromHome,
    hasUserInteracted,
    dataReady,
  ]);

  const handleFilterClick = (filter: FilterDDLItem) => {
    setOpenedFromPill(false);
    configureForFilterKey(filter.key);
    setFilterBy(filter.key);
    setFilterLogic(
      getAppliedFilterLogic(filter.key, appliedFilters, filterArray)
    );
    setIsFilterOpen(false);
    setTimeout(() => setSecondFilterDisplay(true), 0);
  };

  const handleSecondaryFilterSelect = (
    e: CheckboxChangeEvent,
    option: SecondaryFilterOption
  ) => {
    if (e?.target?.checked) {
      setSelectedOptions((prevState) => {
        const alreadyExists = prevState.findIndex(
          (item) => item.id === option.id
        );
        if (alreadyExists === -1) return [...prevState, option];
        return prevState;
      });
    } else {
      setSelectedOptions((prevState) =>
        prevState.filter((selectedOption) => selectedOption.id !== option.id)
      );
    }
  };

  const handleResetGroup = () => {
    const fb = filterBy;
    if (!fb) return;

    setSelectedOptions([]);
    setAppliedFilters((prev) => prev.filter((f) => f.filterBy !== fb));

    setFilterArray((prevFA: FilterArrayItem[] = []) => {
      const next = prevFA.filter((f) => f.filterBy !== fb);

      if (onFiltersChange) {
        onFiltersChange(next);
      }

      return next;
    });
  };

  const menu = (
    <MainFilterMenu
      topLevelFilters={pageTopLevelFilters}
      filtersDDL={pageGroupedFilters}
      handleFilterClick={handleFilterClick}
      showSearch={false}
      showGroupTitles={false}
      showDividers={false}
    />
  );
  const renderSubMenu = (opts?: {
    hideBackArrow?: boolean;
    maxHeight?: number;
  }) => (
    <SubFilterMenu
      hideBackArrow={opts?.hideBackArrow ?? openedFromPill}
      dropdownMaxHeight={opts?.maxHeight}
      alertTypesCount={0}
      alertOptions={[]}
      onAlertTypesSearch={() => {}}
      onResetGroup={handleResetGroup}
      setSelectedOptions={setSelectedOptions}
      filterBy={filterBy}
      filterTitle={filterTitle}
      filterLogic={filterLogic}
      onFilterLogicChange={setFilterLogic}
      appliedFilters={appliedFilters}
      selectedOptions={selectedOptions}
      secondaryFiltersDDL={secondaryFiltersDDL}
      secondaryFiltersSource={secondaryFiltersSource}
      token={token ?? undefined}
      setSecondaryFiltersSource={setSecondaryFiltersSource}
      setSecondaryFiltersDDL={setSecondaryFiltersDDL}
      handleAlertThresholdSelect={() => {}}
      handleSecondaryFilterSelect={handleSecondaryFilterSelect}
      handleApplyFilters={handleApplyFilters}
      setSecondFilterDisplay={setSecondFilterDisplay}
      setIsFilterOpen={setIsFilterOpen}
    />
  );

  const calendarMenu = (opts?: {
    hideBackArrow?: boolean;
    maxHeight?: number;
  }) => (
    <CalendarFilterMenu
      hideBackArrow={opts?.hideBackArrow ?? openedFromPill}
      editingId={editingId}
      dateFilter={filterBy}
      handleDataFromChild={() => {}}
      filterTitle={filterTitle}
      setSecondFilterDisplay={setSecondFilterDisplay}
      setIsFilterOpen={setIsFilterOpen}
    />
  );

  return (
    <div className='team-courier-filters'>
      <div className='team-courier-filters__anchor'>
        <FilterDropdowns
          openedFromPill={openedFromPill}
          submenuAnchor={submenuAnchor}
          appliedFilters={appliedFilters}
          menu={menu}
          isFilterOpen={isFilterOpen}
          setIsFilterOpen={setIsFilterOpen}
          secondFilterDisplay={secondFilterDisplay}
          setSecondFilterDisplay={setSecondFilterDisplay}
          filterBy={filterBy}
          isDatePicker={false}
          calendarMenu={calendarMenu}
          renderSubMenu={renderSubMenu}
          zIndex={false}
          showAddFilter={false}
          popupClassName='home-chart-filter-popup'
        />
      </div>
    </div>
  );
};

export default TeamCourierFilters;
